import { useCallback, useSyncExternalStore } from 'react';
import { generateEntityCode } from '@erp/domain/numbering';
import { useCompanySettings } from '@/hooks/use-settings';

/**
 * Offline numbering — keeps the user from EVER seeing "OFFLINE-XXX" as
 * a code/barcode.
 *
 * How it works
 * ────────────
 * The server maintains an atomic counter per scope (article, client,
 * supplier, barcode). When online we just call `consume_entity_code`
 * to claim the next number. Offline we can't do that — but the prewarm
 * already cached `company_settings` (prefix, padding, current next),
 * so we can compute a sensible local value.
 *
 * To prevent two offline creates from picking the same code, we keep a
 * per-scope offset in localStorage. Each successful offline create
 * advances it by 1. Once the outbox drains successfully, the server's
 * counter has caught up and we reset the offset back to 0 — see
 * `resetOfflineCounter()`.
 *
 * Race semantics: if a second device creates online while we're offline,
 * the offline numbers we showed locally may collide on the server. The
 * sync engine's create handler doesn't pass `code` (we leave it to the
 * server to assign), so the server-side BEFORE-INSERT trigger picks the
 * REAL next code on sync. The optimistic UI may briefly show a
 * placeholder that differs from what eventually lands — that's fine,
 * the cache refreshes when the create succeeds.
 *
 * The KEY POINT for UX: the user never sees "OFFLINE-…", just a real,
 * monotonically-increasing code that matches their numbering scheme.
 */

export type CodeScope = 'article' | 'client' | 'supplier' | 'barcode';

const STORAGE_KEY = 'erp.offline-counters.v1';

type Counters = Partial<Record<CodeScope, number>>;

const listeners = new Set<() => void>();

function read(): Counters {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Counters;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function write(next: Counters): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* localStorage full / private mode — ignore, we just lose the counter */
  }
  listeners.forEach((fn) => fn());
}

export function getOfflineCounter(scope: CodeScope): number {
  return read()[scope] ?? 0;
}

/** Bump the offset by one — call after every queued offline create. */
export function bumpOfflineCounter(scope: CodeScope): number {
  const cur = read();
  const next = (cur[scope] ?? 0) + 1;
  write({ ...cur, [scope]: next });
  return next;
}

/** Reset to 0 — call after the outbox drains successfully for that scope. */
export function resetOfflineCounter(scope: CodeScope): void {
  const cur = read();
  if (cur[scope] === undefined || cur[scope] === 0) return;
  const { [scope]: _, ...rest } = cur;
  write(rest);
}

/** React hook returning the current offset, re-renders when it changes. */
export function useOfflineCounter(scope: CodeScope): number {
  const subscribe = useCallback((cb: () => void) => {
    listeners.add(cb);
    return () => listeners.delete(cb);
  }, []);
  const getSnapshot = useCallback(() => getOfflineCounter(scope), [scope]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

interface ScopeMeta {
  prefix: string;
  next: number;
  padding: number;
}

function metaForScope(
  scope: CodeScope,
  settings: ReturnType<typeof useCompanySettings>['data'] | undefined,
): ScopeMeta | null {
  if (!settings) return null;
  switch (scope) {
    case 'article':  return { prefix: settings.articleCodePrefix,  next: settings.articleCodeNext,  padding: settings.articleCodePadding };
    case 'client':   return { prefix: settings.clientCodePrefix,   next: settings.clientCodeNext,   padding: settings.clientCodePadding };
    case 'supplier': return { prefix: settings.supplierCodePrefix, next: settings.supplierCodeNext, padding: settings.supplierCodePadding };
    case 'barcode':  return { prefix: settings.barcodePrefix,      next: settings.barcodeNext,      padding: settings.barcodePadding };
  }
}

/**
 * Compute the next code locally, including the offline offset. Returns
 * `null` if company-settings haven't loaded yet (caller can fall through
 * to its own loading state).
 */
export function useOfflineNextCode(scope: CodeScope): string | null {
  const { data: settings } = useCompanySettings();
  const offset = useOfflineCounter(scope);
  const meta = metaForScope(scope, settings);
  if (!meta) return null;
  return generateEntityCode(meta.prefix, meta.next + offset, meta.padding);
}
